<script>
  import { createEventDispatcher } from "svelte";
  import {
    DirectCalendarEventTypes,
    FixedCalendarEventCategories,
  } from "../../../Utils/utilConstants.svelte";
  import {
    getDisplayDate,
    getDisplayPatientName,
    getDisplaySurgeonName,
    getDoctorName,
    getPatientAge,
    getTime,
    isObjectEmpty,
  } from "../../../Utils/utilFunctions.svelte";

  export let showMonthlyTooltip = false,
    isDoctorCalendar = false,
    isMonthView = false,
    tooltipInfo = {};

  let tooltipStyle = "",
    initDone = false,
    displayEncounterInfo = [],
    eventInfo = {};

  const dispatch = createEventDispatcher();

  const initTooltip = async () => {
    if (initDone) return;

    eventInfo = tooltipInfo.event.extendedProps;
    if (eventInfo?.eventType === "encounter") {
      await organizeEncounterTooltipInfo(eventInfo);
    }

    tooltipStyle = "";
    const eventRect = tooltipInfo.el.getBoundingClientRect();
    const bottomPosition = window.innerHeight - eventRect.top;
    tooltipStyle += `bottom: ${bottomPosition}px; `;
    if (eventRect.left + eventRect.right > window.innerWidth) {
      tooltipStyle += `right: ${window.innerWidth - eventRect.right}px; `;
    } else {
      tooltipStyle += `left: ${eventRect.left}px; `;
    }
    tooltipStyle += `background: linear-gradient(to right, ${eventInfo.eventColor} 10px, #ffffff 10px); `;
    initDone = true;
  };

  $: if (!showMonthlyTooltip) {
    clearInfo();
  }

  const clearInfo = () => {
    tooltipStyle = "";
    displayEncounterInfo = [];
    initDone = false;
  };

  const getOneRowPatientInfoText = (patient) => {
    if (isObjectEmpty(patient)) return "";
    const infoArr = [];
    infoArr.push(getDisplayPatientName(patient));
    infoArr.push(getPatientAge(patient.birthDate));
    infoArr.push(patient.sexName);
    return infoArr.join(", ");
  };

  const organizeEncounterTooltipInfo = async () => {
    displayEncounterInfo = [];

    if (isDoctorCalendar) {
      // for DoctorView
      if (eventInfo.mergedEncounters.length) {
        // for merged encounters
        // show only time for monthly view
        // show each ect with 2 rows, patient combined info and procedure, for daily view

        if (isMonthView) {
          displayEncounterInfo.push({
            title: "Time:",
            content: `${getTime(eventInfo?.fromDate)} - ${getTime(
              eventInfo?.toDate
            )}`,
          });
        } else
          eventInfo.mergedEncounters.forEach((ect) => {
            displayEncounterInfo.push({
              title: "Patient",
              content: getOneRowPatientInfoText(ect.patient),
            });
            if (ect.procedureName)
              displayEncounterInfo.push({
                title: "Procedure",
                content: ect.procedureName,
              });
          });
      } else {
        // show detailed procedure details (for one encounter)
        // patient name, age, gender, procedure
        displayEncounterInfo.push({
          title: "Patient",
          content: getDisplayPatientName(eventInfo.encounterItem?.patient),
        });
        displayEncounterInfo.push({
          title: "Age",
          content: getPatientAge(eventInfo.encounterItem?.patient?.birthDate),
        });
        displayEncounterInfo.push({
          title: "Gender",
          content: eventInfo.encounterItem?.patient?.sexName,
        });
        if (eventInfo.encounterItem?.procedureName)
          displayEncounterInfo.push({
            title: "Procedure",
            content: eventInfo.encounterItem?.procedureName,
          });
      }
    } else {
      // for non doctor view
      displayEncounterInfo.push({
        title: "Time:",
        content: `${getTime(eventInfo?.fromDate)} - ${getTime(
          eventInfo?.toDate
        )}`,
      });

      displayEncounterInfo.push({
        title: "Doctor",
        content: getDoctorName(eventInfo.encounterItem?.doctor),
      });

      // if (
      //   !eventInfo.mergedEncounters.length &&
      //   !isObjectEmpty(eventInfo.encounterItem.patient)
      // )
      //   displayEncounterInfo.push({
      //     title: "Patient",
      //     content: getOneRowPatientInfoText(eventInfo.encounterItem.patient),
      //   });

      if (eventInfo.encounterItem?.surgeon)
        displayEncounterInfo.push({
          title: "Surgeon",
          content: getDisplaySurgeonName(eventInfo.encounterItem.surgeon),
        });

      if (eventInfo.encounterItem?.hospital?.name)
        displayEncounterInfo.push({
          title: "Hospital",
          content: eventInfo.encounterItem.hospital.name,
        });

      if (
        !eventInfo.mergedEncounters.length &&
        !isObjectEmpty(eventInfo.encounterItem.procedureName)
      )
        displayEncounterInfo.push({
          title: "Procedure",
          content: eventInfo.encounterItem.procedureName,
        });
    }
  };
</script>

{#if showMonthlyTooltip}
  <div
    class="fixed z-max tooltip"
    use:initTooltip
    style={tooltipStyle}
    on:mouseenter={() => dispatch("mouseEnter")}
    on:mouseleave={() => dispatch("mouseLeave")}
  >
    {#if initDone}
      {#if eventInfo?.eventType === "encounter"}
        <span class="font-medium">
          Patient Encounter
          {eventInfo.mergedEncounters.length
            ? " (" + eventInfo.mergedEncounters.length + ")"
            : ""}
        </span>
        {#each displayEncounterInfo as displayItem}
          <div class="flex flex-row">
            <span class="font-medium">{displayItem.title}:&nbsp;</span>
            {displayItem.content}
          </div>
        {/each}
      {:else if eventInfo.eventType === DirectCalendarEventTypes.PublicHoliday}
        <span class="font-medium">
          {FixedCalendarEventCategories[eventInfo.eventType]}
        </span>
        <div class="flex flex-row">
          {tooltipInfo?.event?.extendedProps?.displayTitle ?? ""}
        </div>
      {:else if eventInfo.eventType === DirectCalendarEventTypes.Global}
        <span class="font-medium">
          {FixedCalendarEventCategories[eventInfo.eventType]}
        </span>
        <div class="flex flex-row">
          {tooltipInfo?.event?.extendedProps?.description ?? ""}
        </div>
      {:else}
        <span class="font-medium">
          {FixedCalendarEventCategories[eventInfo.eventType]}
        </span>
        <div class="flex flex-row">
          <span class="font-medium">Doctor:&nbsp;</span>
          {eventInfo.doctorName ?? ""}
        </div>
        <div class="flex flex-row">
          {#if tooltipInfo?.event?.allDay}
            <span class="font-medium">Date:&nbsp;</span>
            {getDisplayDate(eventInfo?.fromDate)}
            -
            {getDisplayDate(eventInfo?.toDate)}
          {:else}
            <span class="font-medium">Time:&nbsp;</span>
            {getTime(eventInfo?.fromDate)}
            -
            {getTime(eventInfo?.toDate)}
          {/if}
        </div>
        {#if eventInfo.eventType === DirectCalendarEventTypes.List}
          <div class="flex flex-row">
            <span class="font-medium">Encounters:&nbsp;</span>
            {eventInfo.nestedEncounter?.length ?? 0}
          </div>
          {#if eventInfo.surgeonName}
            <div class="flex flex-row">
              <span class="font-medium">Surgeon:&nbsp;</span>
              {eventInfo.surgeonName}
            </div>
          {/if}
          {#if eventInfo.hospitalName}
            <div class="flex flex-row">
              <span class="font-medium">Hospital:&nbsp;</span>
              {eventInfo.hospitalName}
            </div>
          {/if}
        {/if}
        {#if eventInfo?.description}
          <div class="flex flex-row">
            <span class="font-medium">Description:&nbsp;</span>
            {eventInfo.description}
          </div>
        {/if}
      {/if}
    {/if}
  </div>
{/if}

<style>
  .tooltip {
    display: flex;
    flex-direction: column;
    align-items: start;
    background-color: rgb(243 244 246);
    color: #000;
    box-shadow: 0px 3px 24px rgba(20, 46, 110, 0.4);
    padding: 12px 12px 12px 20px;
    font-size: 15px;
    border-radius: 0.5rem;
    overflow: hidden;
    min-width: 200px;
    transform: none;
    max-width: 45vw;
    margin-bottom: 6px;
  }
</style>
